import { Check } from '#components/Check'
import { MagicWandIcon } from '#components/MagicWandIcon'
import { Show } from '#components/Show'
import { Tag } from '#components/Tag'
import { UpgradeModal } from '#components/UpgradeModal'
import { decode, log, removeSpecialChars } from '#helpers'
import {
  endpoints,
  request,
  selectors,
  useCtas,
  usePop,
  useProductDetails,
  useSiteConfig,
  useTasks,
} from '#hooks'
import {
  Button,
  Flex,
  Icon,
  PanelBody,
  PanelRow,
  Spinner,
  __experimentalInputControl as InputControl,
  __experimentalInputControlSuffixWrapper as InputControlSuffixWrapper,
} from '@wordpress/components'
import { useEffect, useState } from '@wordpress/element'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export const PerfectTitle = ({ pop }) => {
  const { t } = useTranslation()

  const { tasks, isValidating, mutate } = useTasks(pop)
  const { post } = usePop(pop)
  const { availableCtas } = useCtas()
  const { productInfo } = useProductDetails()
  const { siteConfig } = useSiteConfig()

  const { watch, handleSubmit, control, setValue } = useForm({
    defaultValues: { title: '' },
  })

  const [isWaiting, setIsWaiting] = useState(false)
  const [focusKeyword, setFocusKeyword] = useState('')
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [triesLeft, setTriesLeft] = useState(siteConfig?.openai.triesLeft)
  const [isSameContent, setIsSameContent] = useState(false)
  const [hasMaxTries, setHasMaxTries] = useState(false)

  const condition =
    tasks?.['keyontitle'].pass &&
    tasks?.['longtitlesingle'].pass &&
    tasks?.['notitlesingle'].pass

  const includesKeyword = tasks?.['keyontitle'].pass
  const hasAiFeature = tasks?.['notitlesingle'].aiFeature
  const aiSuggestions = productInfo?.features.aiSuggestions

  const upgradeCta = availableCtas?.find(
    (cta) => cta.id === 'wordpress-upgrade'
  )

  const MAX_LENGTH = 70
  const MIN_LENGTH = 30
  const siteNameLength = window.marketgoo.siteName.length + 3
  const maxLengthFinal = MAX_LENGTH - siteNameLength

  const hasAppropiateLength =
    watch('title').length >= MIN_LENGTH && watch('title').length <= MAX_LENGTH

  const openUpgradeModal = () => {
    setShowUpgradeModal(true)
  }
  const closeUpgradeModal = () => {
    setShowUpgradeModal(false)
  }

  const setTitleAndGetKeyword = () => {
    if (post) {
      setValue('title', decode(post.title.rendered))
      setFocusKeyword(post.postMetaFields?.[selectors.keyword]?.[0])
    }
  }

  const onGenerateTitle = () => {
    setIsWaiting(true)
    request(
      endpoints.metatagsGenerator.create({
        url: post.link,
        ...(focusKeyword && { keyword: focusKeyword }),
      })
    )
      .then((response) => {
        if (response.data.errors) {
          if (response.data.errors[0].code === 403) {
            setHasMaxTries(true)
            setIsSameContent(false)
            setTriesLeft(0)
          }
          throw new Error(
            `Error code ${response.data.errors[0].code}: ${response.data.errors[0].title}`
          )
        }
        if (
          removeSpecialChars(response.data.response.title) ===
          removeSpecialChars(watch('title'))
        ) {
          setIsSameContent(true)
        } else {
          setIsSameContent(false)
          setValue('title', response.data.response.title)
        }
        setTriesLeft(response.data.triesLeft)
      })
      .finally(() => {
        setIsWaiting(false)
      })
      .catch(log)
  }

  const onSubmit = (data) => {
    request(endpoints.pop.edit({ ...pop, data }))
      .then(() => {
        mutate()
      })
      .catch(log)
      .finally(() => {
        window.onbeforeunload = null
        window.location.reload()
      })
  }

  useEffect(setTitleAndGetKeyword, [post])

  return (
    <PanelBody
      title={t('sidebar.task.addtitle.title')}
      icon={isValidating ? <Spinner /> : <Check condition={condition} />}
      initialOpen={!condition}
    >
      <PanelRow>
        <form className="perfect-title" onSubmit={handleSubmit(onSubmit)}>
          <p>{t('sidebar.task.addtitle.intro')}</p>
          <ul>
            <li>
              <Flex justify="flex-start">
                <Check condition={hasAppropiateLength} />
                <span>
                  {t('sidebar.task.add.tittle.request1', {
                    minChars: MIN_LENGTH,
                    maxChars: MAX_LENGTH,
                  })}
                </span>
              </Flex>
            </li>
            <li>
              <Flex justify="flex-start">
                <Check condition={includesKeyword} />
                <span>{t('sidebar.task.add.tittle.request2')}</span>
              </Flex>
            </li>
          </ul>
          <p>
            {t('sidebar.perfecttitle.help', {
              siteName: window.marketgoo.siteName,
              count: siteNameLength,
            })}
          </p>
          <Controller
            name="title"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <InputControl
                help={
                  <Flex wrap={true} align="flex-start" gap="1">
                    <span
                      className={`perfect-title__input-help${
                        isSameContent || hasMaxTries
                          ? ' perfect-title__input-help--error'
                          : ''
                      }`}
                    >
                      {t('sidebar.task.addtitle.field.helper.counter', {
                        tries: aiSuggestions - triesLeft || 0,
                        maxtries: aiSuggestions,
                      }) + ' '}
                      <Show when={isSameContent}>
                        {t('sidebar.task.addtitle.field.helper.samecontent')}
                      </Show>
                      <Show when={hasMaxTries}>
                        {t('sidebar.task.addtitle.field.helper.maxtries')}
                      </Show>
                    </span>
                    <span>{`${watch('title').length}/${MAX_LENGTH}`}</span>
                  </Flex>
                }
                label={
                  <Flex justify="flex-start">
                    <span>{t('sidebar.task.addtitle.field.title')}</span>
                    <Show when={hasAiFeature}>
                      <Tag variant="pro">
                        {t('sidebar.task.addtitle.tag.ai')}
                      </Tag>
                    </Show>
                  </Flex>
                }
                maxLength={maxLengthFinal}
                suffix={
                  <Show when={hasAiFeature}>
                    <InputControlSuffixWrapper
                      onClick={upgradeCta ? openUpgradeModal : onGenerateTitle}
                    >
                      {isWaiting ? (
                        <Spinner style={{ color: '#AC18B4' }} />
                      ) : (
                        <Icon
                          style={{ cursor: 'pointer' }}
                          icon={<MagicWandIcon />}
                        />
                      )}
                    </InputControlSuffixWrapper>
                  </Show>
                }
                {...field}
              />
            )}
          />
          <Button
            type="submit"
            variant="primary"
            isBusy={isWaiting}
            disabled={isWaiting || isValidating}
          >
            {t('sidebar.perfecttitle.cta')}
          </Button>
        </form>
        <Show when={showUpgradeModal}>
          <UpgradeModal
            closeModal={closeUpgradeModal}
            cta={upgradeCta?.attributes}
          />
        </Show>
      </PanelRow>
    </PanelBody>
  )
}
